import styled from "styled-components";
import { useState, useEffect, useRef } from 'react';

import { Typography, Icon, COLORS } from '@virkesborsen/ui-component-library';

import VBLeaflet from '@virkesborsen/vb-js-commons/lib/components/new/vbLeaflet';
import { swapCoords } from '@virkesborsen/vb-js-commons/lib/components/new/leafletConfig';

const RootContainer = styled.div`
    height: calc(${props => props.viewportHeight}px - 80px);
    @media (min-width: 992px) {
        height: ${props => props.viewportHeight}px;
    }
`

const IconHover = styled.div`
    &:hover {
        div svg path {
            fill: ${props => props.active ? 'white' : COLORS['charcoal-500']};
        }
    }
`

const ToolContainer = styled.div`
    margin-bottom: 46px;
    @media (min-width: 992px) {
        margin-bottom: 24px;
    }
`

export default function EntryDetail(props) {
    const leaflet = useRef(null);

    const [sBorder, setSBorder] = useState(L.featureGroup([]));
    const [sGeom, setSGeom] = useState(L.featureGroup([]));
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const waitForMapThenSetData = async () => {
        if(!leaflet.current?.state?.map) {
            setTimeout(() => waitForMapThenSetData(), 100)
        } else {
            setGeom(true)
        }
    }

    useEffect(() => {
        // This is for safari users to fix a weird scrolling behaviour from Safari itself
        document.body.classList.add('fixed-top');
        // This is for the URL bar in mobile browsers for when the appears/dissapears and the viewport of the window changes.
        window.addEventListener('resize', () => setViewportHeight(window.innerHeight));

        waitForMapThenSetData()

        return () => {
            document.body.classList.remove('fixed-top');
            window.removeEventListener('resize', () => setViewportHeight(window.innerHeight));
        }
    }, []);

    useEffect(() => {
        leaflet.current?.state?.map && props.border && setGeom(false)
    }, [props.border])

    const setGeom = (zoomIn = false) => {
        let map = leaflet.current.state.map
        let geomFG = L.featureGroup([])
        let border = L.featureGroup([])

        leaflet.current.clearMap()

        if(props.border) {
            props.border.map(b => {
                let layer = L.polygon(swapCoords(b), {color: "#FFFFFF", fillOpacity: 0, interactive: false, type: 'polygon'})
                leaflet.current.setLayer(layer)
                layer.addTo(border)
            })
        }
        props.geom.forEach((geom) => {
            if(geom.type === 'Polygon') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                    type: 'polygon'
                }

                const polygon = {
                    type: "Feature",
                    geometry: geom
                };
                
                let layer = L.polygon(swapCoords(geom.coordinates), baseStyle)
                layer.addTo(geomFG)
            }

            if(geom.type === 'LineString') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                }

                const lineString = {
                    type: "Feature",
                    geometry: geom
                }

                let layer = L.geoJSON(lineString, baseStyle)
                layer.addTo(geomFG)
            }

            if(geom.type === 'MultiPolygon') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                }
                const multiPolygon = {
                    type: "Feature",
                    geometry: geom
                };
                
                let layer = L.geoJSON(multiPolygon, baseStyle)
                layer.addTo(geomFG)
            }

            if(geom.type === 'MultiPoint') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                }
                const multiPoint = {
                    type: "Feature",
                    geometry: geom
                };
                
                let layer = L.geoJSON(multiPoint, baseStyle)
                layer.addTo(geomFG)
            }
            
            if(geom.type === 'Point') {
                const icon = L.icon({
                    iconUrl: `${Window.static_path}img/map-markers/marker-location.svg`,
                    iconSize: [40, 57],
                    shadowSize: [40, 57],
                    iconAnchor: [20, 57],
                    shadowAnchor: [40, 57],
                    popupAnchor: [0, -32]
                })

                let marker = L.marker([geom.coordinates[1], geom.coordinates[0]], { icon: icon, type: 'marker' })

                marker.addTo(geomFG)
            }
        })
        

        leaflet.current.setLayers(geomFG)

        setSGeom(geomFG)
        setSBorder(border)
        geomFG.addTo(map)
        border.addTo(map)
        zoomIn && map.fitBounds(geomFG.getBounds())
    }

    return <RootContainer className='position-relative overflow-hidden w-100' viewportHeight={viewportHeight}>
        <VBLeaflet ref={leaflet} id='entry_details_map' baseMap='Mapbox Satelite' />

        <div className='position-absolute z-1010 top-0 start-0' style={{margin: '16px'}}>
            <div role='button' onClick={() => window.history.back()} className='d-flex justify-content-center align-items-center rounded rounded-2 bg-white border border-1 border-pebble-50 px-2' style={{height: '32px'}}>
                <Icon name='ChevronDownSimple' rotation={90} size="large" />
                <Typography variant='labelMediumMedium'>Tillbaka</Typography>
            </div>
        </div>

        <ToolContainer className='position-absolute z-1010 bottom-0 start-50 translate-middle-x'>
            <div className='d-flex justify-content-center align-items-center text-center'>
                <IconHover active={true} role='button' className='p-2 bg-white hover-bg-green-spruce-800 rounded rounded-2 hover-white me-2' style={{width: '65px'}} onClick={() => leaflet.current.state.map.fitBounds(sGeom.getBounds())}>
                    <Icon name='InfoFilled' color='charcoal-500' size='medium'/>
                    <Typography variant='tooltipRegular'>Ärende</Typography>
                </IconHover>

                <IconHover active={props.border} role='button' className={`p-2 ${props.border ? 'bg-white hover-bg-green-spruce-800 hover-white' : 'bg-pebble-100'} rounded rounded-2`} style={{width: '65px'}} onClick={() => props.border && leaflet.current.state.map.fitBounds(sBorder.getBounds())}>
                    <Icon name='HouseSimple' color='charcoal-500' size='medium'/>
                    <Typography variant='tooltipRegular'>Fastighet</Typography>
                </IconHover>
            </div>
        </ToolContainer>
    </RootContainer>
};
